// Story 8.10 — PUT /api/v1/creators/me/identity.
//
// ---------------------------------------------------------------------------
// WHY THIS EXISTS, AND WHY IT LIVES IN THE PAYOUT FEATURE
// ---------------------------------------------------------------------------
//
// A self-billed settlement voucher must name its counterparty (bogføringsloven
// § 9), so `POST /payout/request` refuses outright when the creator has no legal
// identity on file, and its message points the creator at "Your details" on the
// payout page. Until this endpoint existed that remedy was unreachable: the
// apply endpoint and the draft-upsert both refuse an approved applicant, and the
// admin review service only mutates `pending` rows. A creator approved before
// the identity block was collected could be told what to do and had no way to
// do it.
//
// It lives HERE rather than in `creator-application/` because the surface that
// needs it is the payout panel — the only place a creator is told the block is
// missing, and the only place the consequence bites.
//
// ⚠ AN IDEMPOTENCY KEY IS REQUIRED, and not because this moves money. The
// server's idempotency middleware registers the prefix `/api/v1/creators/me`
// and prefix-matches every POST/PUT/PATCH beneath it, so this inherits replay
// protection from the same rule that covers `PATCH /creators/me`. The key is
// regenerated PER SUBMIT, not per mount — the Stories 1.16/1.18/8.5 pattern.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import {
  PAYOUT_OPTIONS_QUERY_KEY,
  type StoredCreatorIdentity,
} from "./usePayoutOptions";

export type UpdateIdentityError = {
  code: string;
  message: string;
  /** `AppError::Validation` names the offending field, so the form can put the
   *  message under the input it belongs to rather than in a banner. */
  field?: string;
};

/** The wire shape, which is deliberately IDENTICAL to the identity half of the
 *  apply body — one block, one contract, both paths. */
export type UpdateIdentityVariables = {
  legal_name: string;
  address_line1: string;
  address_line2?: string | null;
  postal_code: string;
  city: string;
  country_code: string;
  business_status: "private" | "business";
  business_registration_number?: string | null;
  idempotencyKey: string;
};

export type UpdateIdentityResult = {
  data: {
    application_id: string;
    identity: StoredCreatorIdentity;
    changed_fields: string[];
  };
};

async function putIdentity(
  vars: UpdateIdentityVariables,
): Promise<UpdateIdentityResult> {
  const { idempotencyKey, ...body } = vars;
  const response = await fetch("/api/v1/creators/me/identity", {
    method: "PUT",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: UpdateIdentityError = {
      code: "IDENTITY_UPDATE_FAILED",
      message: `Could not save your details (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) detail = { ...raw.error };
    } catch {
      // Body not JSON — keep the generic message.
    }
    throw detail;
  }
  return (await response.json()) as UpdateIdentityResult;
}

export function useUpdateCreatorIdentity() {
  const queryClient = useQueryClient();
  return useMutation<UpdateIdentityResult, UpdateIdentityError, UpdateIdentityVariables>({
    mutationFn: putIdentity,
    onSuccess: () => {
      // The payout options payload carries BOTH the stored block and the
      // `counterparty_identity_on_file` flag that gates `can_request`, so a
      // successful save has to re-read it — otherwise the panel keeps showing
      // "identity not on file" against a request button that would now succeed.
      queryClient.invalidateQueries({ queryKey: PAYOUT_OPTIONS_QUERY_KEY });
    },
  });
}
